import React, { useState, useContext, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Home, 
  Settings, 
  Shield, 
  School, 
  BookOpen,
  FileText, 
  Flag,
  MessageSquare
} from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import ThemeContext from '../contexts/ThemeContext';
import { THEMES } from '../utils/theme';
import MeritDisplay from '../components/MeritDisplay';

/**
 * Moderator layout component for moderator pages
 * Includes navigation, header, and content area
 * Moderators can only manage their own school's data
 */
const ModeratorLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  useEffect(() => {
    const fetchPendingCounts = async () => {
      if (!user) return;
      
      try {
        // Get school ID
        const schoolId = user?.school?.id || user?.schoolId;
        
        if (!schoolId) {
          console.error('No school ID found for moderator');
          return;
        }
        
        // Fetch pending course requests
        const courseResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/course-requests?status=pending`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Fetch pending section requests  
        const sectionResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/section-requests?status=pending`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Fetch pending reports for this school
        const reportsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        let totalRequestsCount = 0;
        let totalReportsCount = 0;
        
        if (courseResponse.ok) {
          const courseData = await courseResponse.json();
          totalRequestsCount += courseData.requests?.length || 0;
        }
        
        if (sectionResponse.ok) {
          const sectionData = await sectionResponse.json();
          totalRequestsCount += sectionData.requests?.length || 0;
        }

        if (reportsResponse.ok) {
          const reportsData = await reportsResponse.json();
          // Handle both array and object responses
          if (Array.isArray(reportsData)) {
            totalReportsCount = reportsData.length;
          } else if (reportsData.reports && Array.isArray(reportsData.reports)) {
            totalReportsCount = reportsData.reports.length;
          } else if (reportsData.total) {
            totalReportsCount = reportsData.total;
          }
        }
        
        setPendingRequestsCount(totalRequestsCount);
        setPendingReportsCount(totalReportsCount);
      } catch (error) {
        console.error('Error fetching pending counts:', error);
      }
    };
    
    if (user) {
      fetchPendingCounts();
      
      // Refresh counts every 30 seconds
      const interval = setInterval(fetchPendingCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Theme toggle handler
  const handleThemeToggle = () => {
    // Cycle through themes: default -> dark -> light -> default
    const themes = [THEMES.DEFAULT, THEMES.DARK, THEMES.LIGHT];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  // Ensure only moderators can access
  if (user?.role !== 'moderator') {
    navigate('/school');
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-blue-800 text-white transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:w-64`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between p-4 border-b border-blue-700">
          <div className="flex items-center space-x-2">
            <Shield size={24} />
            <span className="text-xl font-bold">Moderator Panel</span>
          </div>
          <button 
            className="p-1 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={toggleSidebar}
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Sidebar content */}
        <nav className="mt-5 px-2">
          <NavLink 
            to="/moderator" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Home className="mr-3" size={20} />
            <span>Dashboard</span>
          </NavLink>

          <NavLink 
            to="/moderator/requests" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <MessageSquare className="mr-3" size={20} />
            <span>Requests</span>
            {pendingRequestsCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
              </span>
            )}
          </NavLink>

          <NavLink 
            to="/moderator/reports" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Flag className="mr-3" size={20} />
            <span>Reports</span>
            {pendingReportsCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                {pendingReportsCount > 99 ? '99+' : pendingReportsCount}
              </span>
            )}
          </NavLink>
                    
          <NavLink 
            to="/moderator/school" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <School className="mr-3" size={20} />
            <span>My School</span>
          </NavLink>
          
          <NavLink 
            to="/moderator/courses" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <BookOpen className="mr-3" size={20} />
            <span>Courses</span>
          </NavLink>
          
          <NavLink 
            to="/moderator/professors" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <User className="mr-3" size={20} />
            <span>Professors</span>
          </NavLink>
          
          <NavLink 
            to="/moderator/materials" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-blue-700 text-white' 
                  : 'text-gray-100 hover:bg-blue-700'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <FileText className="mr-3" size={20} />
            <span>Materials</span>
          </NavLink>
          
          <div className="border-t border-blue-700 my-4"></div>
          
          <NavLink 
            to="/school" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-blue-700`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Home className="mr-3" size={20} />
            <span>Return to App</span>
          </NavLink>
          
          <NavLink 
            to="/profile" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-blue-700`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <User className="mr-3" size={20} />
            <span>Profile</span>
          </NavLink>
          
          <NavLink 
            to="/settings" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-blue-700`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Settings className="mr-3" size={20} />
            <span>Settings</span>
          </NavLink>
          
          <button 
            onClick={handleLogout}
            className="flex w-full items-center px-4 py-2 mt-2 text-sm text-gray-100 rounded-lg hover:bg-blue-700"
          >
            <LogOut className="mr-3" size={20} />
            <span>Logout</span>
          </button>
        </nav>
        {/* Alpha Version Disclaimer */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-red-700 text-white rounded-lg p-3 text-xs shadow-md">
            <div className="font-bold text-center mb-1">ALPHA VERSION</div>
            <p>This is an early version of the platform. Features may not work perfectly and may change without notice.</p>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Menu size={24} />
                </button>
                <div className="ml-4 lg:ml-0 flex items-center">
                  <Shield className="text-blue-600 mr-2" size={20} />
                  <div className="text-lg font-semibold text-blue-600">Moderator Dashboard</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <MeritDisplay />
                <button
                  onClick={handleThemeToggle}
                  className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                >
                  {theme === THEMES.DARK ? (
                    <span>☀️</span>
                  ) : (
                    <span>🌙</span>
                  )}
                </button>
                
                <div className="relative">
                  <div className="flex items-center">
                    <div className="ml-3">
                      <div className="text-sm font-medium">
                        {user?.name} (Moderator)
                      </div>
                      {user?.schoolName && (
                        <div className="text-xs text-gray-500">
                          {user.schoolName}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ModeratorLayout;